import { BANK_ACCOUNT_KIND_LABELS, type BankAccount } from "@station/contracts";
import { Money } from "@station/core";
import {
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  MoneyText,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Landmark, Pencil, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { BankAccountForm } from "../../../../features/treasury/bank-account-form.js";
import { useBankAccounts } from "../../../../features/treasury/definitions.js";
import { TreasuryPage } from "../../../../features/treasury/shared.js";
import { formatIban } from "../../../../features/treasury/treasury-values.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatDate } from "../../../../lib/format.js";
import { usePermissions } from "../../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/bankalar/")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: BankAccountsPage,
});

function BankAccountsPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [creating, setCreating] = useState(false);
  return (
    <TreasuryPage
      title={t("menu.bankAccounts")}
      description={t("treasury.bankAccounts.description")}
      actions={
        permissions.has("treasury.definition.manage") ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("treasury.bankAccounts.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <BankAccountList companyId={companyId} creating={creating} onCreatingChange={setCreating} />
      )}
    </TreasuryPage>
  );
}

function BankAccountList({
  companyId,
  creating,
  onCreatingChange,
}: {
  companyId: string;
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const canManage = usePermissions().has("treasury.definition.manage");
  const accounts = useBankAccounts(companyId, { includeInactive: true });
  const [editing, setEditing] = useState<BankAccount | null>(null);

  const columns = useMemo<DataTableColumn<BankAccount>[]>(
    () => [
      {
        id: "name",
        header: t("treasury.fields.name"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <Link
              to="/finans/bankalar/$id"
              params={{ id: row.id }}
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.name}
            </Link>
            {row.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
          </span>
        ),
        exportValue: (row) => row.name,
      },
      {
        id: "bankName",
        header: t("treasury.fields.bank"),
        cell: (row) => row.bankName,
        exportValue: (row) => row.bankName,
      },
      {
        id: "iban",
        header: t("treasury.fields.iban"),
        cell: (row) => <span className="font-mono text-xs">{formatIban(row.iban)}</span>,
        exportValue: (row) => formatIban(row.iban),
      },
      {
        id: "kind",
        header: t("treasury.fields.kind"),
        cell: (row) => BANK_ACCOUNT_KIND_LABELS[row.kind],
        exportValue: (row) => BANK_ACCOUNT_KIND_LABELS[row.kind],
      },
      {
        id: "balance",
        header: t("treasury.fields.balance"),
        align: "right",
        cell: (row) => <MoneyText value={row.balance} colorize className="font-medium" />,
        exportValue: (row) => Money.fromMinor(row.balance).formatTr({ symbol: false }),
      },
      {
        id: "availableBalance",
        header: t("treasury.fields.availableBalance"),
        align: "right",
        cell: (row) => <MoneyText value={row.availableBalance} colorize />,
        exportValue: (row) => Money.fromMinor(row.availableBalance).formatTr({ symbol: false }),
      },
      {
        id: "overdraftLimit",
        header: t("treasury.fields.overdraftLimit"),
        align: "right",
        defaultVisible: false,
        cell: (row) =>
          row.overdraftLimit === "0" ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <MoneyText value={row.overdraftLimit} />
          ),
        exportValue: (row) => Money.fromMinor(row.overdraftLimit).formatTr({ symbol: false }),
      },
      {
        id: "lastStatementDate",
        header: t("treasury.fields.lastStatement"),
        cell: (row) => <span className="tabular">{formatDate(row.lastStatementDate)}</span>,
        exportValue: (row) => formatDate(row.lastStatementDate),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              align: "right" as const,
              cell: (row: BankAccount) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditing(row);
                  }}
                  aria-label={t("treasury.bankAccounts.editOne", { name: row.name })}
                >
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, canManage],
  );

  return (
    <>
      <DataTable
        caption={t("menu.bankAccounts")}
        columns={columns}
        rows={accounts.data ?? []}
        rowKey={(row) => row.id}
        loading={accounts.isPending}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Landmark />}
            title={t("treasury.bankAccounts.emptyTitle")}
            description={t("treasury.bankAccounts.emptyDescription")}
            action={
              canManage ? (
                <Button onClick={() => onCreatingChange(true)}>
                  <Plus aria-hidden="true" />
                  {t("treasury.bankAccounts.create")}
                </Button>
              ) : undefined
            }
          />
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: "banka-hesaplari",
            sheetName: t("menu.bankAccounts"),
            columns: visible,
            rows: accounts.data ?? [],
          });
        }}
        onRowClick={(row) => void navigate({ to: "/finans/bankalar/$id", params: { id: row.id } })}
      />
      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          onCreatingChange(false);
          setEditing(null);
        }}
        title={editing ? t("treasury.bankAccounts.edit") : t("treasury.bankAccounts.create")}
        description={t("treasury.bankAccounts.dialogDescription")}
        className="sm:max-w-2xl"
      >
        <BankAccountForm
          key={editing?.id ?? "new"}
          companyId={companyId}
          {...(editing ? { account: editing } : {})}
          onSaved={(saved) => {
            toast({ tone: "success", title: t("treasury.bankAccounts.saved", { name: saved.name }) });
            onCreatingChange(false);
            setEditing(null);
          }}
          onCancel={() => {
            onCreatingChange(false);
            setEditing(null);
          }}
        />
      </Dialog>
    </>
  );
}
